/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import type { Transport } from "@modelcontextprotocol/sdk/shared/transport.js";
import express from "express";
import { LocalContext } from "../../cli.js";
import {
  ConsoleLoggerLevel,
  createConsoleLogger,
} from "../../console-logger.js";
import { MCPServerFlags } from "../../flags.js";
import { createMCPServer } from "../../server.js";
import { buildSDK, buildTransportErrorResponse } from "../../tools.js";

import { landingPageExpress } from "../../../landing-page.js";

interface ServeCommandFlags extends MCPServerFlags {
  readonly port: number;
  readonly "disable-static-auth": boolean;
  readonly "log-level": ConsoleLoggerLevel;
  readonly env?: [string, string][];
}

export async function main(this: LocalContext, flags: ServeCommandFlags) {
  flags.env?.forEach(([key, value]) => {
    process.env[key] = value;
  });

  await startStreamableHTTP(flags);
}

async function startStreamableHTTP(cliFlags: ServeCommandFlags) {
  const logger = createConsoleLogger(cliFlags["log-level"]);
  const app = express();

  // Enable CORS for cross-origin requests
  app.use((req, res, next) => {
    res.header("Access-Control-Allow-Origin", "*");
    res.header("Access-Control-Allow-Methods", "GET, POST, DELETE, OPTIONS");
    res.header(
      "Access-Control-Allow-Headers",
      [
        "Accept",
        "Content-Type",
        "Last-Event-ID",
        "Mcp-Session-Id",
        "UapiKey",
        "uapikey",
        "UapiAdminBearerAuth",
        "uapi-admin-bearer-auth",
      ].join(", "),
    );
    res.header("Access-Control-Expose-Headers", "Mcp-Session-Id");
    if (req.method === "OPTIONS") {
      res.sendStatus(204);
      return;
    }
    next();
  });

  app.use(express.json());

  app.use((error: unknown, req: express.Request, res: express.Response, next: express.NextFunction) => {
    if (req.path !== "/mcp") {
      next(error);
      return;
    }

    logger.error("Invalid MCP HTTP request body", {
      error: error instanceof Error ? error.message : String(error),
    });

    if (!res.headersSent) {
      const transportError = buildTransportErrorResponse(error);
      res.status(transportError.status).json(transportError.body);
      return;
    }

    next(error);
  });

  app.all("/mcp", async (req, res) => {
    try {
      const headers = new Headers();
      for (const [key, value] of Object.entries(req.headers)) {
        if (Array.isArray(value)) {
          for (const v of value) headers.append(key, v);
        } else if (value !== undefined) {
          headers.set(key, value);
        }
      }

      const transport = new StreamableHTTPServerTransport();

      const { server: mcpServer } = createMCPServer({
        logger,
        allowedTools: cliFlags.tool,
        dynamic: cliFlags.mode === "dynamic",
        scopes: cliFlags.scope,
        serverURL: cliFlags["server-url"],
        getSDK: () =>
          buildSDK(headers, cliFlags, cliFlags["disable-static-auth"], logger),
        serverIdx: cliFlags["server-index"],
      });

      mcpServer.server.onerror = (error) => {
        logger.error("MCP protocol error", {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      };

      let cleanedUp = false;
      const cleanup = async () => {
        if (cleanedUp) {
          return;
        }
        cleanedUp = true;
        await transport.close();
        await mcpServer.close();
      };

      res.on("close", () => {
        void cleanup();
      });

      await mcpServer.connect(transport as Transport);
      await transport.handleRequest(req, res, req.body);
    } catch (error) {
      logger.error("Error handling MCP request", {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      if (!res.headersSent) {
        const transportError = buildTransportErrorResponse(error);
        res.status(transportError.status).json(transportError.body);
      }
    }
  });

  app.get("/", landingPageExpress);

  const httpServer = app.listen(cliFlags.port, "0.0.0.0", () => {
    const ha = httpServer.address();
    const host = typeof ha === "string" ? ha : `${ha?.address}:${ha?.port}`;
    logger.info("MCP Streamable HTTP server started", { host });
  });

  const shutdown = () => {
    logger.info("Shutting down HTTP server");

    const timer = setTimeout(() => {
      logger.info("Forcing shutdown");
      process.exit(1);
    }, 5000);

    httpServer.close(() => {
      clearTimeout(timer);
      logger.info("Graceful shutdown complete");
      process.exit(0);
    });
  };

  process.on("SIGTERM", shutdown);
  process.on("SIGINT", shutdown);
}
